import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, MapPin, Clock, Calendar, Filter, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SmallGroupsPage() {
  const { t } = useTranslation();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dayFilter, setDayFilter] = useState('all');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [signupData, setSignupData] = useState({ name: '', email: '', phone: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);

  const categories = ['men', 'women', 'youth', 'couples', 'mixed', 'seniors'];
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await axios.get(`${API}/small-groups`);
      setGroups(response.data);
    } catch (error) {
      console.error('Error fetching small groups:', error);
      // Set some placeholder groups for display
      setGroups([
        { id: '1', name: 'Men\'s Bible Study', description: 'Weekly study of Scripture with fellowship', leader: 'John Smith', day: 'Tuesday', time: '7:00 PM', location: 'Main Campus - Room 101', category: 'Men', current_members: 8 },
        { id: '2', name: 'Women\'s Fellowship', description: 'Growing together in faith and friendship', leader: 'Maria Garcia', day: 'Wednesday', time: '10:00 AM', location: 'Community Center', category: 'Women', current_members: 12 },
        { id: '3', name: 'Young Adults', description: 'For ages 18-30, life discussions and faith', leader: 'Carlos Martinez', day: 'Friday', time: '7:30 PM', location: 'Youth Building', category: 'Youth', current_members: 15 },
        { id: '4', name: 'Couples Connection', description: 'Strengthening marriages through faith', leader: 'Pastor & Mrs. Delgado', day: 'Saturday', time: '6:00 PM', location: 'Main Campus', category: 'Couples', current_members: 6 },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/small-groups/${selectedGroup.id}/signup`, {
        ...signupData,
        group_id: selectedGroup.id
      });
      setSignupSuccess(true);
      toast.success(t('smallGroups.signupSuccess'));
    } catch (error) {
      if (error.response?.data?.detail?.includes('Already')) {
        toast.error('Already signed up for this group');
      } else {
        toast.error(t('common.error'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredGroups = groups.filter(group => {
    if (categoryFilter !== 'all' && group.category?.toLowerCase() !== categoryFilter) return false;
    if (dayFilter !== 'all' && group.day?.toLowerCase() !== dayFilter) return false;
    return true;
  });

  const closeDialog = () => {
    setSelectedGroup(null);
    setSignupSuccess(false);
    setSignupData({ name: '', email: '', phone: '', message: '' });
  };

  return (
    <div className="min-h-screen bg-cream pt-24" data-testid="small-groups-page">
      {/* Hero */}
      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="w-16 h-16 bg-olive rounded-full flex items-center justify-center mx-auto mb-6">
              <Users size={32} className="text-white" />
            </div>
            <span className="text-xs uppercase tracking-[0.2em] text-gold mb-4 block">{t('smallGroups.findGroup')}</span>
            <h1 className="font-serif text-4xl md:text-5xl text-charcoal mb-4">{t('smallGroups.title')}</h1>
            <p className="text-charcoal/70 max-w-2xl mx-auto">
              {t('smallGroups.subtitle')}
            </p>
          </motion.div>

          {/* Filters */}
          <div className="flex flex-wrap gap-4 justify-center mb-8">
            <div className="flex items-center gap-2">
              <Filter size={18} className="text-charcoal/50" />
              <span className="text-sm text-charcoal/70">{t('smallGroups.filterBy')}:</span>
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-40 bg-white" data-testid="category-filter">
                <SelectValue placeholder={t('smallGroups.allCategories')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('smallGroups.allCategories')}</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{t(`smallGroups.categories.${cat}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={dayFilter} onValueChange={setDayFilter}>
              <SelectTrigger className="w-40 bg-white" data-testid="day-filter">
                <SelectValue placeholder={t('smallGroups.allDays')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('smallGroups.allDays')}</SelectItem>
                {days.map(day => (
                  <SelectItem key={day} value={day}>{t(`smallGroups.days.${day}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Groups Grid */}
          {filteredGroups.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-6">
              {filteredGroups.map((group, i) => (
                <motion.div
                  key={group.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="bg-white rounded-2xl p-6 border border-gold/10 hover:border-gold/30 transition-all duration-300"
                  data-testid={`group-card-${i}`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <span className="text-xs uppercase tracking-wider text-gold bg-gold/10 px-2 py-1 rounded-full">
                        {group.category}
                      </span>
                      <h3 className="font-serif text-xl text-charcoal mt-2">{group.name}</h3>
                    </div>
                    <div className="text-right">
                      <span className="text-sm text-charcoal/50">{group.current_members || 0} members</span>
                    </div>
                  </div>
                  
                  <p className="text-charcoal/70 text-sm mb-4">{group.description}</p>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm text-charcoal/60">
                      <Users size={16} className="text-burgundy" />
                      <span>{t('smallGroups.leader')}: {group.leader}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-charcoal/60">
                      <Calendar size={16} className="text-gold" />
                      <span>{group.day}s at {group.time}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-charcoal/60">
                      <MapPin size={16} className="text-olive" />
                      <span>{group.location}</span>
                    </div>
                  </div>
                  
                  <Button
                    onClick={() => setSelectedGroup(group)}
                    className="w-full bg-burgundy hover:bg-purple text-white"
                    data-testid={`join-group-${i}`}
                  >
                    {t('smallGroups.joinGroup')}
                  </Button>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-2xl border border-gold/10">
              <Users size={48} className="mx-auto text-charcoal/20 mb-4" />
              <p className="text-charcoal/50">{t('smallGroups.noGroups')}</p>
            </div>
          )}
        </div>
      </section>

      {/* Why Join Section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl text-charcoal">{t('smallGroups.whyJoin')}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: t('smallGroups.benefit1'), desc: t('smallGroups.benefit1Desc') },
              { title: t('smallGroups.benefit2'), desc: t('smallGroups.benefit2Desc') },
              { title: t('smallGroups.benefit3'), desc: t('smallGroups.benefit3Desc') },
            ].map((benefit, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-12 h-12 bg-burgundy/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users size={24} className="text-burgundy" />
                </div>
                <h3 className="font-serif text-lg text-charcoal mb-2">{benefit.title}</h3>
                <p className="text-charcoal/70 text-sm">{benefit.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Signup Dialog */}
      <Dialog open={!!selectedGroup} onOpenChange={closeDialog}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {signupSuccess ? t('common.success') : t('smallGroups.signupTitle')}
            </DialogTitle>
          </DialogHeader>
          
          {signupSuccess ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-olive rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-white" />
              </div>
              <p className="text-charcoal/70">{t('smallGroups.signupSuccess')}</p>
              <Button onClick={closeDialog} className="mt-6 bg-burgundy hover:bg-purple text-white">
                {t('common.close')}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSignup} className="space-y-4 mt-4">
              {selectedGroup && (
                <div className="bg-cream p-4 rounded-xl mb-4">
                  <p className="font-medium text-charcoal">{selectedGroup.name}</p>
                  <p className="text-sm text-charcoal/60">{selectedGroup.day}s at {selectedGroup.time}</p>
                </div>
              )}
              <div>
                <Label>{t('common.name')}</Label>
                <Input
                  value={signupData.name}
                  onChange={(e) => setSignupData({...signupData, name: e.target.value})}
                  required
                  className="bg-cream"
                />
              </div>
              <div>
                <Label>{t('common.email')}</Label>
                <Input
                  type="email"
                  value={signupData.email}
                  onChange={(e) => setSignupData({...signupData, email: e.target.value})}
                  required
                  className="bg-cream"
                />
              </div>
              <div>
                <Label>{t('common.phone')}</Label>
                <Input
                  type="tel"
                  value={signupData.phone}
                  onChange={(e) => setSignupData({...signupData, phone: e.target.value})}
                  required
                  className="bg-cream"
                />
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-burgundy hover:bg-purple text-white"
              >
                {isSubmitting ? t('common.loading') : t('smallGroups.joinGroup')}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
